/**
 * Customer plan upgrade (contract, signature, payment, activation). The owner (Stargate + Cashier) decides
 * every value; the Nave only presents them. Strict allow-list: anything not listed is dropped and a
 * malformed upgrade invalidates the response.
 */
export type UpgradeStatus = "creating" | "awaiting_signature" | "awaiting_payment" | "active" | "failed" | "cancelled";

export type CustomerPlanUpgrade = Readonly<{
  upgradeId: string;
  status: UpgradeStatus;
  quote: Readonly<{
    serviceName: string; currency: string; currentLabel: string; targetLabel: string; targetAmount: string;
    amountDueToday: string; nextCycleDate: string; remainingDays: number; totalDays: number;
  }>;
  signer?: Readonly<{ name: string; email: string }>;
  invoiceId?: string;
  checkoutUrl?: string;
  failureCode?: string;
  activatedAt?: string;
}>;

export type UpgradeEligibility = Readonly<{ selfService: boolean; reason?: string; signer?: Readonly<{ name: string; email: string }> }>;

export type StartUpgradeInput = Readonly<{ offerId: string; requestId: string; expectedAmount: string }>;

const record = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);
const text = (value: unknown, max: number): string | undefined => typeof value === "string" && value.trim() !== "" && value.length <= max ? value.trim() : undefined;
const decimal = (value: unknown): string | undefined => typeof value === "string" && /^\d{1,12}\.\d{2}$/u.test(value) ? value : undefined;
const isoDate = (value: unknown): string | undefined => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/u.test(value) ? value : undefined;
const uuid = (value: unknown): string | undefined => typeof value === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(value) ? value : undefined;
const STATUSES: readonly string[] = ["creating", "awaiting_signature", "awaiting_payment", "active", "failed", "cancelled"];

/** Only the Cashier's hosted checkout (https, /pagar/<token>) may be opened from the portal. */
const cashierCheckout = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  try {
    const url = new URL(value);
    return url.protocol === "https:" && url.hostname === "cashier.cupcode.com.br" && !url.username && !url.password
      && /^\/pagar\/[A-Za-z0-9_-]{16,200}$/u.test(url.pathname) ? url.href : undefined;
  } catch { return undefined; }
};

const projectSigner = (value: unknown): CustomerPlanUpgrade["signer"] => {
  if (!record(value)) return undefined;
  const name = text(value.name, 160), email = text(value.email, 160);
  return name && email ? { name, email } : undefined;
};

export const upgradeProjection = (input: unknown): CustomerPlanUpgrade | undefined => {
  if (!record(input) || !record(input.quote)) return undefined;
  const q = input.quote;
  // Owner shape ({current,target}) on the BFF; the BFF's own flat shape (currentLabel...) when the browser re-validates it.
  const current = record(q.current) ? q.current : { label: q.currentLabel };
  const target = record(q.target) ? q.target : { label: q.targetLabel, amount: q.targetAmount };
  const upgradeId = uuid(input.upgradeId);
  const status = typeof input.status === "string" && STATUSES.includes(input.status) ? (input.status as UpgradeStatus) : undefined;
  const serviceName = text(q.serviceName, 160), currentLabel = text(current.label, 60), targetLabel = text(target.label, 60);
  const targetAmount = decimal(target.amount), amountDueToday = decimal(q.amountDueToday), nextCycleDate = isoDate(q.nextCycleDate);
  const currency = typeof q.currency === "string" && /^[A-Z]{3}$/u.test(q.currency) ? q.currency : undefined;
  const remainingDays = typeof q.remainingDays === "number" && Number.isInteger(q.remainingDays) ? q.remainingDays : undefined;
  const totalDays = typeof q.totalDays === "number" && Number.isInteger(q.totalDays) ? q.totalDays : undefined;
  if (!upgradeId || !status || !serviceName || !currentLabel || !targetLabel || !targetAmount || !amountDueToday || !nextCycleDate || !currency || !remainingDays || !totalDays) return undefined;
  const signer = projectSigner(input.signer);
  const invoiceId = uuid(input.invoiceId), checkoutUrl = cashierCheckout(input.checkoutUrl);
  const failureCode = text(input.failureCode, 80), activatedAt = typeof input.activatedAt === "string" && !Number.isNaN(Date.parse(input.activatedAt)) ? input.activatedAt : undefined;
  return {
    upgradeId, status,
    quote: { serviceName, currency, currentLabel, targetLabel, targetAmount, amountDueToday, nextCycleDate, remainingDays, totalDays },
    ...(signer ? { signer } : {}), ...(invoiceId ? { invoiceId } : {}), ...(checkoutUrl ? { checkoutUrl } : {}),
    ...(failureCode ? { failureCode } : {}), ...(activatedAt ? { activatedAt } : {}),
  };
};

/** `{ upgrade: null | upgrade }` of the "latest" endpoint. */
export const latestUpgradeProjection = (input: unknown): Readonly<{ upgrade: CustomerPlanUpgrade | null }> | undefined => {
  if (!record(input) || !("upgrade" in input)) return undefined;
  if (input.upgrade === null) return { upgrade: null };
  const upgrade = upgradeProjection(input.upgrade);
  return upgrade ? { upgrade } : undefined;
};

export const eligibilityProjection = (input: unknown): UpgradeEligibility | undefined => {
  if (!record(input) || typeof input.selfService !== "boolean") return undefined;
  const signer = projectSigner(input.signer), reason = text(input.reason, 60);
  return { selfService: input.selfService, ...(reason ? { reason } : {}), ...(signer ? { signer } : {}) };
};

/** One entry point for the BFF: picks the projection of each upgrade resource. */
export const upgradesResponseProjection = (resource: "upgrades" | "upgrade-latest" | "upgrade-eligibility", body: unknown): unknown =>
  resource === "upgrade-latest" ? latestUpgradeProjection(body) : resource === "upgrade-eligibility" ? eligibilityProjection(body) : upgradeProjection(body);

export type StepState = "done" | "current" | "todo";

/** Where an in-flight upgrade stands: sign the contract, pay the pro-rata amount, plan active. */
export const upgradeSteps = (status: UpgradeStatus): readonly [StepState, StepState, StepState] =>
  status === "active" ? ["done", "done", "done"]
    : status === "awaiting_payment" ? ["done", "current", "todo"]
      : status === "awaiting_signature" ? ["current", "todo", "todo"]
        : ["todo", "todo", "todo"];
